/**
 * Display formatting for the Plans & Billing section. All date copy follows
 * the Figma strings ("Feb, 15 2027" in the plan summary, "Feb 15" in the
 * coupon card and cancellation modals).
 */

const LOCALE = 'en-US';

const toDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const monthShort = new Intl.DateTimeFormat(LOCALE, { month: 'short' });
const dayNumeric = new Intl.DateTimeFormat(LOCALE, { day: 'numeric' });
const yearNumeric = new Intl.DateTimeFormat(LOCALE, { year: 'numeric' });

/** ``MMM, D YYYY`` → "Feb, 15 2027". Returns '' for missing/invalid input. */
export function formatPlanDate(value) {
    const date = toDate(value);
    if (!date) return '';
    return `${monthShort.format(date)}, ${dayNumeric.format(date)} ${yearNumeric.format(date)}`;
}

/** ``MMM D`` → "Feb 15". Returns '' for missing/invalid input. */
export function formatShortDate(value) {
    const date = toDate(value);
    if (!date) return '';
    return `${monthShort.format(date)} ${dayNumeric.format(date)}`;
}

/** ``MMM D, YYYY`` → "Feb 15, 2027" (the cancellation toast, 9261:7040). Returns '' for missing/invalid input. */
export function formatLongDate(value) {
    const date = toDate(value);
    if (!date) return '';
    return `${monthShort.format(date)} ${dayNumeric.format(date)}, ${yearNumeric.format(date)}`;
}

/** Localised currency string for banners: (4900, 'usd') → "$49.00". */
export function formatMoney(amountCents, currency) {
    const cents = Number(amountCents);
    if (!Number.isFinite(cents)) return '';
    const code = (currency || 'usd').toUpperCase();
    try {
        return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: code }).format(cents / 100);
    } catch {
        return `${(cents / 100).toFixed(2)} ${code}`;
    }
}

/**
 * The balance split for the Figma figure (9756:29157): a small "$" sign next to a
 * large light number. ``{ currency: '$', value: '28.97' }``. A balance that a running
 * meter or an enforcement-exempt god pushed below $0 keeps its sign on the number
 * (``{ currency: '$', value: '-39.53' }``), the same "$-39.53" the Credit Usage
 * Spend Summary shows, so the two tabs never disagree.
 */
export function splitCreditBalance(tokens) {
    const cents = Number(tokens?.balance_cents) || 0;
    const magnitude = formatMoney(Math.abs(cents), 'usd').slice(1);
    return { currency: '$', value: cents < 0 ? `-${magnitude}` : magnitude };
}

/** Display of the prepaid balance as plain text: "$28.97", or "$-39.53" below $0. */
export function formatCreditBalance(tokens) {
    const { currency, value } = splitCreditBalance(tokens);
    return `${currency}${value}`;
}

/** "Expires on 11/2030" style ``MM/YYYY``. */
export function formatCardExpiry(expMonth, expYear) {
    const month = Number(expMonth);
    const year = Number(expYear);
    if (!Number.isFinite(month) || !Number.isFinite(year)) return '';
    return `${String(month).padStart(2, '0')}/${year}`;
}

/** "visa" → "Visa"; "amex" → "Amex". */
export function formatCardBrand(brand) {
    if (!brand) return 'Card';
    const text = String(brand).replace(/[_-]+/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Pick the date the plan's current access ends: the paid-through date for
 * live subscriptions, ``ended_at`` once Stripe has closed it.
 */
export function getPlanEndDate(plan) {
    if (!plan) return null;
    if (plan.status === 'canceled') return plan.ended_at || plan.current_period_end || null;
    return plan.current_period_end || null;
}
